import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
	Container, Card, CardBody, Badge, Input, Row, Col, Button, Label
} from 'reactstrap';
import Select from 'react-select';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Action } from 'redux';
import { ApplicationState } from 'store';
import { formatDistanceToNow } from 'date-fns';
import { ToastContainer } from 'react-toastify';

import {
	getMeta,
	render,
	CHANNELS,
	getClickAction
} from 'helpers/notifications/notificationConfig';
import { getNotifications, markAsRead } from 'store/notifications/action';
import { NotificationActionTypes } from 'store/notifications/types';
import { initialState } from 'store/notifications/reducer';
import { useNavigate } from 'react-router-dom';
import { formatReadableDate } from 'helpers/date';
import { withTranslation } from 'react-i18next';

const NotificationCenter: React.FC = ({t}: any) => {
	const navigate = useNavigate();
	const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
	const notifications = useSelector((s: ApplicationState) => s.notifications.centerList);
	const pagination = useSelector((s: ApplicationState) => s.notifications.pagination);
	const loading = useSelector((s: ApplicationState) => s.notifications.centerLoading);

	const [queryInput, setQueryInput] = useState('');
	const [query, setQuery] = useState('');
	const [typeFilter, setTypeFilter] = useState('');
	const [channelFilter, setChannelFilter] = useState('');
	const [page, setPage] = useState(1);

	const lastRef = useRef<HTMLDivElement | null>(null);
	const observer = useRef<IntersectionObserver | null>(null);

	const TYPE_OPTIONS = [
		{ value: '', label: 'All Types' },
		{ value: 'events', label: 'Events' },
		{ value: 'messages', label: 'Messages' },
		{ value: 'updates', label: 'Updates' },
	];

	const CHANNEL_OPTIONS = [
		{ value: '', label: 'All Channels' },
		...CHANNELS.map(ch => ({
			value: ch.key,      // 'inApp', 'email', etc.
			label: ch.label,    // 'In-App', 'Email', etc.
		})),
	];

	const debouncedSetQuery = useRef(
		debounce((val: string) => {
			setQuery(val);
			setPage(1);
		}, 300)
	).current;

	useEffect(() => { debouncedSetQuery(queryInput); }, [queryInput]);

	// reset list on filters change
	useEffect(() => {
		dispatch({
			type: NotificationActionTypes.GET_NOTIFICATIONS_SUCCESS,
			payload: { content: [], pagination: initialState.pagination },
			meta: { scope: 'center', append: false }
		});

		dispatch(getNotifications({
			page: 1,
			query,
			type: typeFilter,
			channel: channelFilter,
			append: false,
			scope: 'center',
		}));
		setPage(1);
	}, [dispatch, query, typeFilter, channelFilter]);

	// load more
	useEffect(() => {
		if (page > 1) {
			dispatch(getNotifications({ page, query, type: typeFilter, channel: channelFilter, append: true, scope: 'center' }));
		}
	}, [dispatch, page]);

	const loadMore = useCallback(() => {
		if (!loading && pagination.hasNext) setPage(p => p + 1);
	}, [loading, pagination.hasNext]);

	useEffect(() => {
		observer.current?.disconnect();
		observer.current = new IntersectionObserver(entries => { if (entries[0].isIntersecting) loadMore(); });
		if (lastRef.current) observer.current.observe(lastRef.current);
	}, [notifications, loadMore]);

	const handleReset = () => {
		setQueryInput(''); setQuery(''); setTypeFilter(''); setChannelFilter(''); setPage(1);
		dispatch(getNotifications({ page: 1, append: false, scope: 'center' }));
	};

	return (
		<div className="page-content">
			<ToastContainer closeButton />
			<Container fluid>
				<Card className="p-4 border-0 shadow-sm">
					{/* Filters */}
					<div className="mb-4 bg-light px-3 py-3 rounded">
						<div className="d-flex justify-content-start align-items-center mb-2 gap-2">
							<h5 className="fw-semibold mb-0">{t("common.table.filters")}</h5>
							<Button
								color="danger"
								className="btn-label btn-sm"
								disabled={!query && !typeFilter && !channelFilter}
								onClick={handleReset}
							>
								<i className="ri-refresh-line label-icon align-middle fs-16 me-2" /> {t("common.offcanvas.reset")}
							</Button>
						</div>

						<Row className="g-3">
							<Col md={4}>
								<Label className="form-label text-muted fw-medium small mb-1">
									<i className="ri-search-line me-1 text-primary" /> {t("common.filter.search.search_text")}
								</Label>
								<Input
									type="text"
									value={queryInput}
									onChange={e => setQueryInput(e.target.value)}
									placeholder={t("common.filter.search.search_notifications")}
								/>
							</Col>
							<Col md={4}>
								<Label className="form-label text-muted fw-medium small mb-1">
									<i className="ri-filter-line me-1 text-info" /> {t("common.filter.search.type")}
								</Label>
								<Select
									options={TYPE_OPTIONS}
									value={TYPE_OPTIONS.find(o => o.value === typeFilter)}
									onChange={(opt: { value: any; }) => setTypeFilter(opt?.value || '')}
									classNamePrefix="react-select"
									isClearable
								/>
							</Col>
							<Col md={4}>
								<Label className="form-label text-muted fw-medium small mb-1">
									<i className="ri-mail-line me-1 text-secondary" /> {t("common.filter.search.channel")}
								</Label>
								<Select
									options={CHANNEL_OPTIONS}
									value={CHANNEL_OPTIONS.find(o => o.value === channelFilter)}
									onChange={(opt: { value: any; }) => setChannelFilter(opt?.value || '')}
									classNamePrefix="react-select"
									isClearable
								/>
							</Col>
						</Row>
					</div>

					{/* Notifications */}
					{notifications.map((n, idx) => {
						const meta = getMeta(n.template_key);
						const { title, subtitle } = render(n.template_key, n.payload || {});
						const d = new Date(n.sent_at ?? n.created_at);
						const isDigest = n.payload?.items && Array.isArray(n.payload.items) && n.payload.items.length > 0;
						const card = (
							<Card
								key={n.id}
								className="mb-2 shadow-sm cursor-pointer"
								onClick={() => {
									if (n.read_status !== 'READ') {
										dispatch(markAsRead(n.id, 'center'));
									}
									const url = getClickAction(n.template_key, n.payload || {});
									if (url.startsWith('http')) {
										window.open(url, '_blank');
									} else {
										navigate(url);
									}
								}}
							>
								<CardBody className="d-flex align-items-start gap-3">
									<i className={`${meta?.icon || 'ri-notification-line text-muted'} fs-4`} />
									<div className="flex-grow-1">
										<div className="fw-semibold">{title}</div>
										{ isDigest ? (
											<ul className="text-muted small">
												{n.payload.items.map((itm: any, i: number) => (
												<li key={i}>
													{itm.invitee_name || itm.task_title} @ {formatReadableDate(itm.start_time || itm.due_date)}
												</li>
												))}
											</ul>
										) : <div
  className="text-muted small"
  dangerouslySetInnerHTML={{ __html: subtitle ?? '' }}
/>}
										<div className="text-muted small mt-1">{formatDistanceToNow(d, { addSuffix: true })}</div>
									</div>
									{n.read_status !== 'READ' && <Badge color="primary" pill>{t("common.modals.new")}</Badge>}
								</CardBody>
							</Card>
						);

						return idx === notifications.length - 1
							? <div ref={lastRef} key={n.id}>{card}</div>
							: card;
					})}

					{/* No Results */}
					{!loading && notifications.length === 0 && (
						<div className="text-center py-5">
							<i className="ri-notification-off-line display-4 text-muted mb-3" />
							<h5 className="fw-semibold">{t("common.modals.no_notifications_found")}</h5>
							<p className="text-muted">{t("common.modals.try_adjusting_search_filters")}</p>
						</div>
					)}

					{/* Loading */}
					{loading && Array.from({ length: 5 }).map((_, i) => (
						<Card key={`sk-${i}`} className="mb-2 shadow-sm border-0">
							<CardBody className="d-flex align-items-start gap-3 placeholder-glow">
								<span className="placeholder bg-secondary rounded-circle" style={{ width: 32, height: 32 }} />
								<div className="flex-grow-1">
									<span className="placeholder col-6 mb-2 bg-secondary d-block" />
									<span className="placeholder col-4 mb-1 bg-secondary d-block" />
									<span className="placeholder col-3 bg-secondary d-block" />
								</div>
							</CardBody>
						</Card>
					))}
				</Card>
			</Container>
		</div>
	);
};

export default withTranslation()(NotificationCenter);
